import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { seatMap } from '@eti/products';

import type { SelectTripResponse, TravelerNew, Trip } from '@eti/schema-types';
import { getPriceValue } from '../../../../utils/addedExtraProducts';
import type { TransformBundleData } from '../../../../utils/transformBundleData';
import { transformBundleData } from '../../../../utils/transformBundleData';
import { GET_SELECTED_TRIP } from '../../graphql/queries';
import { SELECT_TRIP } from '../../graphql/mutations';
import type { AvailableExtraProductsType } from '../../components/v3/branded-fares/model';

export type SelectedTripData = Omit<Trip, 'availableExtraProducts'> & {
  availableExtraProducts: Array<AvailableExtraProductsType>;
  bundleData: TransformBundleData;
  tripTravelers: TravelerNew[];
};

export const useSelectedTrip = () => {
  const [selectedTripData, setSelectedTripData] = useState<SelectedTripData | null>(null);
  const [noSelectedTripError, setNoSelectedTripError] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);

  const { loading: getSelectedTripLoading, refetch } = useQuery(GET_SELECTED_TRIP, {
    variables: { excludedExtraProductIds: [seatMap.id.toString()] },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ selectedTrip }: SelectTripResponse) => {
      setIsRefetching(false);
      if (selectedTrip) {
        const transformedBundleData = transformBundleData(selectedTrip.availableETGFares[0]);
        const formattedData = {
          ...selectedTrip,
          bundleData: transformedBundleData,
          tripTravelers: selectedTrip.travelers,
        } as SelectedTripData;
        setSelectedTripData(formattedData);
      } else {
        setNoSelectedTripError(true);
      }
    },
    onError: () => {
      setNoSelectedTripError(true);
      setIsRefetching(false);
    },
  });

  const [selectTripMutation, { loading: isSelectTripLoading }] = useMutation(SELECT_TRIP, {
    onCompleted: ({ selectTrip: selectTripResponse }) => {
      if (!selectTripResponse.success) {
        setNoSelectedTripError(true);
      }
    },
    onError: () => {
      setNoSelectedTripError(true);
    },
  });

  const handleRefetch = () => {
    setIsRefetching(true);
    refetch();
  };

  const prevBundleSelection = useMemo(() => {
    const prevSelectedBundleOption = selectedTripData?.bundleData?.selectedOption;
    return selectedTripData?.bundleData?.options?.find(
      ({ id }: { id: string }) => id === prevSelectedBundleOption,
    );
  }, [selectedTripData]);

  const selectedExtraProductIds = useMemo(() => {
    const prevBundleSelectionProductIds = prevBundleSelection?.features.map(
      ({ productTypeId }: { productTypeId: string }) => productTypeId,
    );

    return (
      selectedTripData?.availableExtraProducts
        .filter((prod: AvailableExtraProductsType) => {
          const hasPrice = getPriceValue(prod?.sellSpecification);

          return (
            prod.selectedWithTrip &&
            hasPrice &&
            !prevBundleSelectionProductIds?.includes(prod.productId)
          );
        })
        .map((prod) => Number(prod.productId)) ?? []
    );
  }, [selectedTripData, prevBundleSelection]);

  return {
    selectedTripData,
    isSelectTripLoading: getSelectedTripLoading || isSelectTripLoading,
    isRefetching,
    noSelectedTripError,
    handleRefetch,
    selectTrip: selectTripMutation,
    prevBundleSelection,
    selectedExtraProducts: selectedExtraProductIds,
  };
};
